import {
  GameState,
  Position,
  Piece,
  PieceType,
  Board,
  validateMove,
  MoveResult,
  getPieceAt,
  isValidPosition,
  createInitialGameState,
} from './chess-engine';

// ─── Types ──────────────────────────────────────────────────────────────────

export type SuperpowerType =
  | 'nova-strike'
  | 'arcane-shield'
  | 'phantom-step'
  | 'hex-freeze'
  | 'soul-swap'
  | 'time-warp';

export type OrbType = 'mana-gem' | 'speed-rune' | 'berserker-rune' | 'fortify-rune';

export interface PowerOrb {
  position: Position;
  type: OrbType;
}

export interface FrozenPiece {
  position: Position;
  turnsRemaining: number;
}

export interface ShieldedPiece {
  position: Position;
  turnsRemaining: number;
}

export interface ActiveSuperpower {
  type: SuperpowerType;
  /** For powers that need a first target click (e.g. soul-swap first piece) */
  firstTarget?: Position;
  /** Nova strike: next normal move will trigger burst capture */
  pendingBurst?: boolean;
  /** Phantom step: next click chooses destination */
  pendingPhantomFrom?: Position;
}

export interface ArcaneBlitzGameState extends GameState {
  whiteMana: number;
  blackMana: number;
  /** Superpower being "charged" awaiting target square click */
  chargingPower: ActiveSuperpower | null;
  frozenPieces: FrozenPiece[];
  shieldedPieces: ShieldedPiece[];
  fortifiedColor: 'white' | 'black' | null;
  fortifiedTurnsRemaining: number;
  powerOrbs: PowerOrb[];
  superPowerLog: string[];
  movesSinceOrbSpawn: number;
  speedRuneActive: 'white' | 'black' | null;
  berserkerActive: { color: 'white' | 'black'; capturesRemaining: number } | null;
  /** Previous state snapshot for Time Warp undo */
  prevStateSnapshot: Omit<ArcaneBlitzGameState, 'prevStateSnapshot'> | null;
}

// ─── Superpower definitions ──────────────────────────────────────────────────

export interface SuperpowerDef {
  type: SuperpowerType;
  name: string;
  cost: number;
  emoji: string;
  description: string;
  /** Does it need a target square click after activation? */
  needsTarget: boolean;
  /** Does it need TWO target clicks? (soul-swap) */
  needsDoubleTarget: boolean;
}

export const SUPERPOWERS: SuperpowerDef[] = [
  {
    type: 'nova-strike',
    name: 'Nova Strike',
    cost: 2,
    emoji: '💥',
    description: 'Capture any enemy piece within 2 squares – ignores normal move rules.',
    needsTarget: true,
    needsDoubleTarget: false,
  },
  {
    type: 'arcane-shield',
    name: 'Arcane Shield',
    cost: 2,
    emoji: '🛡️',
    description: 'Make one of your pieces invincible for 1 full turn.',
    needsTarget: true,
    needsDoubleTarget: false,
  },
  {
    type: 'phantom-step',
    name: 'Phantom Step',
    cost: 3,
    emoji: '👻',
    description: 'Teleport one of your pieces to any empty square instantly.',
    needsTarget: true,
    needsDoubleTarget: true, // first=from, second=to
  },
  {
    type: 'hex-freeze',
    name: 'Hex Freeze',
    cost: 3,
    emoji: '❄️',
    description: 'Freeze an enemy piece in place for 2 turns – it cannot move.',
    needsTarget: true,
    needsDoubleTarget: false,
  },
  {
    type: 'soul-swap',
    name: 'Soul Swap',
    cost: 4,
    emoji: '🔄',
    description: 'Swap the positions of any two of your own pieces.',
    needsTarget: true,
    needsDoubleTarget: true,
  },
  {
    type: 'time-warp',
    name: 'Time Warp',
    cost: 5,
    emoji: '⏪',
    description: "Undo your opponent's last move – they must play differently.",
    needsTarget: false,
    needsDoubleTarget: false,
  },
];

// ─── Orb definitions ─────────────────────────────────────────────────────────

export interface OrbDef {
  type: OrbType;
  emoji: string;
  name: string;
  colorClass: string;
  description: string;
}

export const ORB_DEFS: OrbDef[] = [
  {
    type: 'mana-gem',
    emoji: '💎',
    name: 'Mana Gem',
    colorClass: 'text-teen-cyan',
    description: '+3 Mana instantly',
  },
  {
    type: 'speed-rune',
    emoji: '⚡',
    name: 'Speed Rune',
    colorClass: 'text-teen-gold',
    description: 'Your next move is free (skip opponent turn)',
  },
  {
    type: 'berserker-rune',
    emoji: '🔥',
    name: 'Berserker Rune',
    colorClass: 'text-teen-red',
    description: '+1 extra Mana on your next 3 captures',
  },
  {
    type: 'fortify-rune',
    emoji: '🛡️',
    name: 'Fortify Rune',
    colorClass: 'text-teen-green',
    description: 'All your pieces are shielded for 1 full turn',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const posEq = (a: Position, b: Position) => a[0] === b[0] && a[1] === b[1];

const deepCloneBoard = (board: Board): Board =>
  board.map(row => row.map(cell => (cell ? { ...cell } : null)));

function spawnOrbs(board: Board, existing: Position[]): PowerOrb[] {
  const orbTypes: OrbType[] = ['mana-gem', 'speed-rune', 'berserker-rune', 'fortify-rune'];
  const orbs: PowerOrb[] = [];
  const occupied = new Set(existing.map(([r, c]) => `${r},${c}`));

  // Collect empty squares in the middle rows
  const candidates: Position[] = [];
  for (let r = 2; r < 6; r++) {
    for (let c = 0; c < 8; c++) {
      if (!board[r][c] && !occupied.has(`${r},${c}`)) {
        candidates.push([r, c]);
      }
    }
  }

  // Shuffle and pick up to 3
  const shuffled = candidates.sort(() => Math.random() - 0.5).slice(0, 3);
  shuffled.forEach((pos, idx) => {
    orbs.push({ position: pos, type: orbTypes[idx % orbTypes.length] });
  });
  return orbs;
}

// ─── Initial state ────────────────────────────────────────────────────────────

export const createInitialArcaneState = (): ArcaneBlitzGameState => {
  const base = createInitialGameState();
  const orbs = spawnOrbs(base.board, []);
  return {
    ...base,
    whiteMana: 3,
    blackMana: 3,
    chargingPower: null,
    frozenPieces: [],
    shieldedPieces: [],
    fortifiedColor: null,
    fortifiedTurnsRemaining: 0,
    powerOrbs: orbs,
    superPowerLog: [],
    movesSinceOrbSpawn: 0,
    speedRuneActive: null,
    berserkerActive: null,
    prevStateSnapshot: null,
  };
};

// ─── Mana helpers ────────────────────────────────────────────────────────────

export const getMana = (state: ArcaneBlitzGameState, color: 'white' | 'black') =>
  color === 'white' ? state.whiteMana : state.blackMana;

export const setMana = (
  state: ArcaneBlitzGameState,
  color: 'white' | 'black',
  value: number
): ArcaneBlitzGameState => ({
  ...state,
  whiteMana: color === 'white' ? Math.max(0, Math.min(10, value)) : state.whiteMana,
  blackMana: color === 'black' ? Math.max(0, Math.min(10, value)) : state.blackMana,
});

export const canActivateSuperpower = (
  state: ArcaneBlitzGameState,
  type: SuperpowerType,
  color: 'white' | 'black'
): boolean => {
  if (state.sideToMove !== color) return false;
  const def = SUPERPOWERS.find(p => p.type === type)!;
  return getMana(state, color) >= def.cost;
};

// ─── Validate arcane move ────────────────────────────────────────────────────

export interface ArcaneMoveResult extends MoveResult {
  newState?: ArcaneBlitzGameState;
  logEntry?: string;
  orbCollected?: OrbType;
}

export const validateArcaneMove = (
  state: ArcaneBlitzGameState,
  from: Position,
  to: Position,
  promotion?: PieceType
): ArcaneMoveResult => {
  const color = state.sideToMove;

  // Check if the moving piece is frozen
  const isFrozen = state.frozenPieces.some(f => posEq(f.position, from) && f.turnsRemaining > 0);
  if (isFrozen) {
    return { isLegal: false, reason: 'This piece is frozen!' };
  }

  const baseResult = validateMove(state, from, to, promotion);
  if (!baseResult.isLegal || !baseResult.newState) return { isLegal: false, reason: baseResult.reason };

  const capturedPiece = getPieceAt(state.board, to);
  const movingPiece = getPieceAt(state.board, from);

  let newState: ArcaneBlitzGameState = {
    ...(baseResult.newState as ArcaneBlitzGameState),
    whiteMana: state.whiteMana,
    blackMana: state.blackMana,
    chargingPower: null,
    frozenPieces: state.frozenPieces,
    shieldedPieces: state.shieldedPieces,
    fortifiedColor: state.fortifiedColor,
    fortifiedTurnsRemaining: state.fortifiedTurnsRemaining,
    powerOrbs: state.powerOrbs,
    superPowerLog: [...state.superPowerLog],
    movesSinceOrbSpawn: state.movesSinceOrbSpawn + 1,
    speedRuneActive: state.speedRuneActive,
    berserkerActive: state.berserkerActive,
    prevStateSnapshot: null,
  };

  // Save snapshot before mutating (for Time Warp)
  newState.prevStateSnapshot = { ...state, prevStateSnapshot: null } as Omit<ArcaneBlitzGameState, 'prevStateSnapshot'>;

  // ── Mana gain ────────────────────────────────────────────────────────────
  let manaGain = 1;
  if (capturedPiece) {
    manaGain += 1; // +1 extra for capture
    // Berserker bonus
    if (state.berserkerActive?.color === color) {
      manaGain += 1;
      const remaining = state.berserkerActive.capturesRemaining - 1;
      newState.berserkerActive = remaining > 0 ? { color, capturesRemaining: remaining } : null;
    }
  }
  newState = setMana(newState, color, getMana(state, color) + manaGain);

  // ── Check if captured piece had a shield ─────────────────────────────────
  if (capturedPiece) {
    const capturedColor = capturedPiece.color;
    const isShielded =
      state.shieldedPieces.some(s => posEq(s.position, to)) ||
      (state.fortifiedColor === capturedColor && state.fortifiedTurnsRemaining > 0);
    if (isShielded) {
      // Shield absorbs the capture — illegal
      return { isLegal: false, reason: "That piece is shielded and can't be captured!" };
    }
  }

  // ── Reduce frozen timers ─────────────────────────────────────────────────
  newState.frozenPieces = state.frozenPieces
    .map(f => ({ ...f, turnsRemaining: f.turnsRemaining - 1 }))
    .filter(f => f.turnsRemaining > 0);

  // ── Reduce shield timers ─────────────────────────────────────────────────
  newState.shieldedPieces = state.shieldedPieces
    .map(s => ({ ...s, turnsRemaining: s.turnsRemaining - 1 }))
    .filter(s => s.turnsRemaining > 0);

  // ── Reduce fortify timer ─────────────────────────────────────────────────
  if (newState.fortifiedTurnsRemaining > 0) {
    newState.fortifiedTurnsRemaining -= 1;
    if (newState.fortifiedTurnsRemaining === 0) {
      newState.fortifiedColor = null;
    }
  }

  // ── Check orb collection ──────────────────────────────────────────────────
  let orbCollected: OrbType | undefined;
  const landedOrb = state.powerOrbs.find(o => posEq(o.position, to));
  if (landedOrb) {
    orbCollected = landedOrb.type;
    newState.powerOrbs = state.powerOrbs.filter(o => !posEq(o.position, to));
    newState = applyOrbEffect(newState, landedOrb.type, color);
    newState.superPowerLog = [`Collected ${ORB_DEFS.find(d => d.type === landedOrb.type)?.name}!`, ...newState.superPowerLog].slice(0, 8);
  }

  // ── Respawn orbs every 8 moves ─────────────────────────────────────────
  if (newState.movesSinceOrbSpawn >= 8 && newState.powerOrbs.length === 0) {
    newState.powerOrbs = spawnOrbs(
      newState.board,
      newState.powerOrbs.map(o => o.position)
    );
    newState.movesSinceOrbSpawn = 0;
  }

  // ── Speed rune: skip opponent turn ────────────────────────────────────────
  if (state.speedRuneActive === color) {
    // Keep sideToMove as current player (override base result)
    newState = { ...newState, sideToMove: color };
    newState.speedRuneActive = null;
  }

  const logEntry = capturedPiece
    ? `${color === 'white' ? '⬜' : '⬛'} Captured ${capturedPiece.type} (+${manaGain} mana)`
    : undefined;

  return { isLegal: true, newState, logEntry, orbCollected };
};

// ─── Orb effects ─────────────────────────────────────────────────────────────

function applyOrbEffect(
  state: ArcaneBlitzGameState,
  orb: OrbType,
  color: 'white' | 'black'
): ArcaneBlitzGameState {
  switch (orb) {
    case 'mana-gem':
      return setMana(state, color, getMana(state, color) + 3);
    case 'speed-rune':
      return { ...state, speedRuneActive: color };
    case 'berserker-rune':
      return { ...state, berserkerActive: { color, capturesRemaining: 3 } };
    case 'fortify-rune':
      return { ...state, fortifiedColor: color, fortifiedTurnsRemaining: 2 };
    default:
      return state;
  }
}

// ─── Activate superpower ─────────────────────────────────────────────────────

export interface PowerActivationResult {
  success: boolean;
  newState?: ArcaneBlitzGameState;
  logEntry?: string;
  error?: string;
  needsMoreInput?: boolean;
}

export const activateSuperpower = (
  state: ArcaneBlitzGameState,
  type: SuperpowerType,
  color: 'white' | 'black',
  targetA?: Position,
  targetB?: Position
): PowerActivationResult => {
  const def = SUPERPOWERS.find(p => p.type === type)!;
  const mana = getMana(state, color);

  if (mana < def.cost) {
    return { success: false, error: `Not enough mana! Need ${def.cost}, have ${mana}.` };
  }

  let newState: ArcaneBlitzGameState = {
    ...state,
    chargingPower: null,
    superPowerLog: state.superPowerLog,
  };

  switch (type) {
    case 'nova-strike': {
      if (!targetA) return { success: false, needsMoreInput: true };
      const target = getPieceAt(state.board, targetA);
      if (!target || target.color === color) {
        return { success: false, error: 'Nova Strike must target an enemy piece!' };
      }
      // Check range: within 2 squares of any of your pieces
      let inRange = false;
      for (let r = 0; r < 8 && !inRange; r++) {
        for (let c = 0; c < 8 && !inRange; c++) {
          const p = state.board[r][c];
          if (p && p.color === color) {
            if (Math.abs(r - targetA[0]) <= 2 && Math.abs(c - targetA[1]) <= 2) {
              inRange = true;
            }
          }
        }
      }
      if (!inRange) {
        return { success: false, error: 'Nova Strike target must be within 2 squares of one of your pieces!' };
      }
      const newBoard = deepCloneBoard(state.board);
      newBoard[targetA[0]][targetA[1]] = null; // destroy target
      newState = setMana({ ...newState, board: newBoard }, color, mana - def.cost);
      const logEntry = `💥 Nova Strike! Blasted ${target.type} at ${String.fromCharCode(97 + targetA[1])}${8 - targetA[0]}`;
      newState.superPowerLog = [logEntry, ...newState.superPowerLog].slice(0, 8);
      return { success: true, newState, logEntry };
    }

    case 'arcane-shield': {
      if (!targetA) return { success: false, needsMoreInput: true };
      const target = getPieceAt(state.board, targetA);
      if (!target || target.color !== color) {
        return { success: false, error: 'Arcane Shield must target one of your own pieces!' };
      }
      newState = setMana(newState, color, mana - def.cost);
      newState.shieldedPieces = [
        ...state.shieldedPieces.filter(s => !posEq(s.position, targetA)),
        { position: targetA, turnsRemaining: 2 },
      ];
      const logEntry = `🛡️ Arcane Shield on ${target.type} at ${String.fromCharCode(97 + targetA[1])}${8 - targetA[0]}`;
      newState.superPowerLog = [logEntry, ...newState.superPowerLog].slice(0, 8);
      return { success: true, newState, logEntry };
    }

    case 'phantom-step': {
      if (!targetA) return { success: false, needsMoreInput: true };
      if (!targetB) {
        // First click selected the piece
        const piece = getPieceAt(state.board, targetA);
        if (!piece || piece.color !== color) {
          return { success: false, error: 'Phantom Step: choose one of your own pieces first!' };
        }
        return { success: false, needsMoreInput: true };
      }
      const piece = getPieceAt(state.board, targetA);
      if (!piece || piece.color !== color) {
        return { success: false, error: 'Invalid Phantom Step source!' };
      }
      const dest = getPieceAt(state.board, targetB);
      if (dest) {
        return { success: false, error: 'Phantom Step destination must be empty!' };
      }
      const newBoard = deepCloneBoard(state.board);
      newBoard[targetB[0]][targetB[1]] = { ...piece };
      newBoard[targetA[0]][targetA[1]] = null;
      newState = setMana({ ...newState, board: newBoard }, color, mana - def.cost);
      const logEntry = `👻 Phantom Step! ${piece.type} teleported to ${String.fromCharCode(97 + targetB[1])}${8 - targetB[0]}`;
      newState.superPowerLog = [logEntry, ...newState.superPowerLog].slice(0, 8);
      return { success: true, newState, logEntry };
    }

    case 'hex-freeze': {
      if (!targetA) return { success: false, needsMoreInput: true };
      const target = getPieceAt(state.board, targetA);
      if (!target || target.color === color) {
        return { success: false, error: 'Hex Freeze must target an enemy piece!' };
      }
      newState = setMana(newState, color, mana - def.cost);
      newState.frozenPieces = [
        ...state.frozenPieces.filter(f => !posEq(f.position, targetA)),
        { position: targetA, turnsRemaining: 4 }, // 4 half-turns = 2 full turns
      ];
      const logEntry = `❄️ Hex Freeze! ${target.type} at ${String.fromCharCode(97 + targetA[1])}${8 - targetA[0]} is frozen!`;
      newState.superPowerLog = [logEntry, ...newState.superPowerLog].slice(0, 8);
      return { success: true, newState, logEntry };
    }

    case 'soul-swap': {
      if (!targetA) return { success: false, needsMoreInput: true };
      if (!targetB) return { success: false, needsMoreInput: true };
      const pieceA = getPieceAt(state.board, targetA);
      const pieceB = getPieceAt(state.board, targetB);
      if (!pieceA || pieceA.color !== color || !pieceB || pieceB.color !== color) {
        return { success: false, error: 'Soul Swap requires two of your own pieces!' };
      }
      const newBoard = deepCloneBoard(state.board);
      newBoard[targetA[0]][targetA[1]] = { ...pieceB };
      newBoard[targetB[0]][targetB[1]] = { ...pieceA };
      newState = setMana({ ...newState, board: newBoard }, color, mana - def.cost);
      const logEntry = `🔄 Soul Swap! ${pieceA.type} ↔ ${pieceB.type}`;
      newState.superPowerLog = [logEntry, ...newState.superPowerLog].slice(0, 8);
      return { success: true, newState, logEntry };
    }

    case 'time-warp': {
      if (!state.prevStateSnapshot) {
        return { success: false, error: "Can't warp time — no previous state recorded!" };
      }
      // Restore previous snapshot but keep current mana minus cost
      const snap = state.prevStateSnapshot as ArcaneBlitzGameState;
      newState = {
        ...snap,
        whiteMana: color === 'white' ? Math.max(0, mana - def.cost) : snap.whiteMana,
        blackMana: color === 'black' ? Math.max(0, mana - def.cost) : snap.blackMana,
        superPowerLog: [`⏪ Time Warp! Enemy move undone.`, ...state.superPowerLog].slice(0, 8),
        prevStateSnapshot: null,
        chargingPower: null,
      } as ArcaneBlitzGameState;
      return { success: true, newState, logEntry: '⏪ Time Warp! Rewound opponent move.' };
    }

    default:
      return { success: false, error: 'Unknown superpower.' };
  }
};

// ─── Utility exports ──────────────────────────────────────────────────────────

export const isFrozen = (state: ArcaneBlitzGameState, pos: Position): boolean =>
  state.frozenPieces.some(f => posEq(f.position, pos));

export const isShielded = (state: ArcaneBlitzGameState, pos: Position): boolean =>
  state.shieldedPieces.some(s => posEq(s.position, pos)) ||
  (() => {
    const p = getPieceAt(state.board, pos);
    return !!(p && state.fortifiedColor === p.color && state.fortifiedTurnsRemaining > 0);
  })();

export const getOrbAt = (state: ArcaneBlitzGameState, pos: Position): PowerOrb | undefined =>
  state.powerOrbs.find(o => posEq(o.position, pos));
